import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { organizationSlice } from 'Flux/Slice/Organization/OrganizationSlice.js'
import { useGetOrganizationsSelector } from 'Flux/Selector/Organization/GetOrganizations/GetOrganizationsSelector.js'
import type { OrganizationProfile } from '../../../Generated/OrganizationProfile.ts'
import type { OrganizationTypeKey } from '../../../Generated/OrganizationType.ts'
import { BuildingOfficeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { OrganizationWidget } from './OrganizationWidget.js'

export interface OrganizationSearchWidgetProps {
  onOrganizationSelected?: (organization: OrganizationProfile) => void
  pageSize?: number
  selectButtonText?: string
}

export const OrganizationSearchWidget: React.FC<OrganizationSearchWidgetProps> = ({
  onOrganizationSelected,
  pageSize = 10,
  selectButtonText
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const organizations = useGetOrganizationsSelector()

  const [filter, setFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [expandedOrgs, setExpandedOrgs] = useState<Record<string, boolean>>({})

  // Load organizations when component mounts or when filter/page changes
  useEffect(() => {
    setIsLoading(true)
    dispatch(
      organizationSlice.actions.listOrganizationsRequest({
        page: currentPage,
        size: pageSize,
        filter
      })
    )
    setIsLoading(false)
  }, [dispatch, currentPage, pageSize, filter])

  // Handle organization selection
  const handleOrganizationClick = useCallback(
    (organization: OrganizationProfile) => {
      if (onOrganizationSelected) {
        onOrganizationSelected(organization)
      } else {
        // Default behavior: navigate to organization page
        navigate(`/organization/${organization.id}`)
      }
    },
    [navigate, onOrganizationSelected]
  )

  // Toggle expanded state for an organization
  const toggleExpanded = useCallback((orgId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    setExpandedOrgs((prev) => ({
      ...prev,
      [orgId]: !prev[orgId]
    }))
  }, [])

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value)
    setCurrentPage(0) // Reset to first page when search changes
  }

  // Handle pagination
  const handleNextPage = () => {
    setCurrentPage((prev) => prev + 1)
  }

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1))
  }

  const getOrganizationTypeDisplay = (type: OrganizationTypeKey) => {
    switch (type) {
      case 'LABORATORY':
        return 'Laboratory'
      case 'CUSTOMER':
        return 'Customer'
      default:
        return type
    }
  }

  return (
    <div className="bg-slate-100 rounded-lg p-4 drop-shadow-sm">
      <div className="mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white"
            placeholder="Search organizations..."
            value={filter}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : organizations.length === 0 ? (
        <div className="text-center p-4 text-gray-500">No organizations found</div>
      ) : (
        <div className="overflow-x-auto overflow-y-auto max-h-[80vh]">
          <table className="table w-full">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org) => (
                <React.Fragment key={org.id}>
                  <tr className="hover:bg-slate-200 cursor-pointer" onClick={(e) => toggleExpanded(org.id, e)}>
                    <td>
                      <div className="flex items-center gap-2">
                        <BuildingOfficeIcon className="w-5 h-5 text-gray-600" />
                        <span>{org.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className={'flex items-center'}>{getOrganizationTypeDisplay(org.type)}</div>
                    </td>
                    <td className="text-right">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleOrganizationClick(org)
                        }}
                      >
                        {selectButtonText || (onOrganizationSelected ? 'SELECT' : 'VIEW PROFILE')}
                      </button>
                    </td>
                  </tr>
                  {expandedOrgs[org.id] && (
                    <tr>
                      <td colSpan={3} className="bg-slate-50 p-4">
                        <OrganizationWidget organization={org} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-between mt-4">
        <button className="btn btn-sm btn-outline" onClick={handlePrevPage} disabled={currentPage === 0}>
          Previous
        </button>
        <span className="text-sm text-gray-600">Page {currentPage + 1}</span>
        <button className="btn btn-sm btn-outline" onClick={handleNextPage} disabled={organizations.length < pageSize}>
          Next
        </button>
      </div>
    </div>
  )
}
